/**
 * Which account the signed-in person is acting for
 * (ai_docs/corporate-user-feature.md §9.1). The default is their own row; a
 * granted organization can be chosen from the account menu, is remembered per
 * person in localStorage, and is dropped the moment the server says the grant
 * is gone.
 *
 * A switch is a hard reset of everything below this provider: the query cache
 * and the market ring buffer are emptied, and `AppRouter` re-keys the hub
 * provider so both hubs re-dial for the new account. Nothing from one account
 * survives into the next — the whole app changes identity, so the cache does
 * not need per-account keys.
 *
 * The non-React layers read the choice through `api/activeAccount.ts`; this
 * provider publishes it there before the first child renders, because a
 * child's first query fires from the child's own commit, ahead of any effect
 * up here.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { AccountSummary, UserMe } from '../../api/client'
import { onAccountAccessLost, setActiveAccountId } from '../../api/activeAccount'
import type { AccountAccessLostReason } from '../../api/activeAccount'
import { clearMarketHistory } from '../realtime/marketHistory'

type AccountState = {
    /** Everything the person may act for — their own account first. */
    accounts: AccountSummary[]
    active: AccountSummary
    isDelegated: boolean
    switchTo: (accountId: string) => void
    /** Return to the personal account. */
    reset: () => void
    /** One-line explanation of an involuntary reset, until dismissed. */
    notice: string | null
    dismissNotice: () => void
}

const storageKey = (actorId: string) => `tradeski.activeAccount.${actorId}`

const readRemembered = (actorId: string): string | null => {
    try {
        return localStorage.getItem(storageKey(actorId))
    } catch {
        return null
    }
}

const writeRemembered = (actorId: string, accountId: string | null): void => {
    try {
        if (accountId) localStorage.setItem(storageKey(actorId), accountId)
        else localStorage.removeItem(storageKey(actorId))
    } catch {
        // Storage unavailable — the choice simply does not persist.
    }
}

const personalAccount = (user: UserMe): AccountSummary =>
    user.accounts.find((a) => a.isSelf) ?? {
        id: user.id,
        displayName: user.email,
        kind: user.kind,
        isSelf: true,
    }

/** The organization with this id, if the person may act for it. Anything else
 *  — a revoked grant, a stale key, their own id — means personal (null). */
const grantedOrganization = (user: UserMe, accountId: string | null): AccountSummary | null =>
    user.accounts.find((a) => a.id === accountId && !a.isSelf) ?? null

const NOTICE: Record<AccountAccessLostReason, (name: string) => string> = {
    removed: (name) => `Access to ${name} was removed.`,
    disabled: () => 'Acting for organizations is currently disabled.',
}

const noop = () => {}

/** Outside the provider — the sign-in screen, component tests — there is no
 *  delegation and nothing to switch to. */
const NO_DELEGATION: AccountState = {
    accounts: [],
    active: { id: '', displayName: '', kind: 'Person', isSelf: true },
    isDelegated: false,
    switchTo: noop,
    reset: noop,
    notice: null,
    dismissNotice: noop,
}

const AccountContext = createContext<AccountState | null>(null)

export const AccountProvider = ({ user, children }: { user: UserMe; children: ReactNode }) => {
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    // The chosen organization, or null for personal. Held as the whole summary
    // so its name is still known if a later /me probe no longer lists it.
    const [selected, setSelected] = useState<AccountSummary | null>(() => {
        const remembered = grantedOrganization(user, readRemembered(user.id))
        setActiveAccountId(remembered?.id ?? null)
        return remembered
    })
    const [notice, setNotice] = useState<string | null>(null)

    const active = useMemo(
        () => (selected && grantedOrganization(user, selected.id)) || personalAccount(user),
        [selected, user],
    )

    const selectedRef = useRef(selected)
    useEffect(() => {
        selectedRef.current = selected
    }, [selected])

    // The one path every change of account takes.
    const apply = useCallback(
        (next: AccountSummary | null) => {
            setActiveAccountId(next?.id ?? null)
            writeRemembered(user.id, next?.id ?? null)
            queryClient.clear()
            clearMarketHistory()
            setSelected(next)
            navigate('/dashboard')
        },
        [navigate, queryClient, user.id],
    )

    const switchTo = useCallback(
        (accountId: string) => {
            const next = grantedOrganization(user, accountId)
            if ((next?.id ?? null) === (selectedRef.current?.id ?? null)) return
            setNotice(null)
            apply(next)
        },
        [apply, user],
    )

    const reset = useCallback(() => {
        if (selectedRef.current) apply(null)
    }, [apply])

    // Access lost for the account we are acting for — a REST 403, a hub
    // negotiate 403, an `accountRevoked` push, or a /me re-probe that no longer
    // lists it (reported by `getMe`). One reset and one notice; a repeat for an
    // account already left is ignored.
    useEffect(
        () =>
            onAccountAccessLost((accountId, reason) => {
                const current = selectedRef.current
                if (!current || current.id !== accountId) return
                setNotice(NOTICE[reason](current.displayName))
                apply(null)
            }),
        [apply],
    )

    // Keep the module accessor honest across every path, and clear it on
    // unmount (sign-out, session loss) so a later /me probe is personal.
    useEffect(() => {
        setActiveAccountId(active.isSelf ? null : active.id)
        return () => setActiveAccountId(null)
    }, [active])

    const dismissNotice = useCallback(() => setNotice(null), [])

    return (
        <AccountContext.Provider
            value={{
                accounts: user.accounts,
                active,
                isDelegated: !active.isSelf,
                switchTo,
                reset,
                notice,
                dismissNotice,
            }}
        >
            {children}
        </AccountContext.Provider>
    )
}

export const useAccount = (): AccountState => useContext(AccountContext) ?? NO_DELEGATION
